import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorDashboard.css";

const DoctorDashboard = () => {
  const { doctorUser, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const { userid } = useParams();
  const [doctor, setDoctor] = useState({ name: "Guest", specialization: "N/A", consultationFee: 0, availability: {}, isVerified: false, accountStatus: 'active' });
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [acceptedAppointments, setAcceptedAppointments] = useState([]);
  const [rescheduledAppointments, setRescheduledAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [totalAppointments, setTotalAppointments] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({ date: "", time: "", reason: "" });
  const [rejectReason, setRejectReason] = useState("");
  const [processingIds, setProcessingIds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const currentDate = new Date();

  useEffect(() => {
    if (loading) return;

    if (!doctorUser || doctorUser.role !== 'doctor' || doctorUser.id !== userid) {
      setError("You are not authorized to view this doctor dashboard.");
      setTimeout(() => navigate('/doctor-login'), 2000);
      setIsLoading(false);
      return;
    }

    const fetchDoctorData = async () => {
      setIsLoading(true);
      try {
        const [profileResponse, appointmentsResponse] = await Promise.all([
          api.get("/doctors/profile", {
            headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
          }),
          api.get(`/appointments/doctor/${userid}`, {
            headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
          }).catch(err => {
            if (err.response?.status === 404) {
              console.warn('No appointments found for this doctor, returning empty array');
              return { data: [] }; // Handle 404 gracefully
            }
            throw err; // Re-throw other errors
          })
        ]);

        // Handle Profile Data
        const doctorData = profileResponse.data;
        setDoctor(doctorData);
        if (doctorData.accountStatus === 'deactivated') {
          setError(`Your account is deactivated. Reason: ${doctorData.deactivationReason}. Please contact the team at turemedicine@gmail.com.`);
          setIsLoading(false);
          return;
        }

        // Handle Appointments Data (will be empty for unverified doctors)
        const appointments = appointmentsResponse.data || [];
        const pending = appointments.filter(
          (app) => app.status.toLowerCase() === "pending" && new Date(app.rescheduledDate || app.date) >= currentDate
        );
        const accepted = appointments.filter(
          (app) => app.status.toLowerCase() === "accepted" && new Date(app.rescheduledDate || app.date) >= currentDate
        );
        const rescheduled = appointments.filter(
          (app) => app.status.toLowerCase() === "rescheduled" && new Date(app.rescheduledDate || app.date) >= currentDate
        );
        const past = appointments.filter(
          (app) => new Date(app.rescheduledDate || app.date) < currentDate
        );

        setPendingAppointments(pending);
        setAcceptedAppointments(accepted);
        setRescheduledAppointments(rescheduled);
        setPastAppointments(past);
        setTotalAppointments(appointments.length);

        const completedAppointments = appointments.filter(app => app.status.toLowerCase() === "completed");
        const netEarnings = completedAppointments.reduce((sum, app) => {
          const fee = app.consultationFee || 0;
          const commissionRate = app.doctorCommissionRate || 10;
          const commission = (fee * commissionRate) / 100;
          const netEarningsForApp = fee - commission;
          return sum + netEarningsForApp;
        }, 0);
        setTotalEarnings(netEarnings);
      } catch (error) {
        console.error('Failed to load doctor data:', error.response?.data || error.message);
        const errorMsg = error.response?.data?.error || error.message;
        setError(`Failed to load dashboard data: ${errorMsg}`);
        if (errorMsg === "Unauthorized: Token expired") {
          setTimeout(() => navigate('/login?type=doctor'), 2000);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDoctorData();
  }, [doctorUser, loading, userid, navigate]);

  const handleAccept = async (appointmentId) => {
    if (processingIds.has(appointmentId)) return;
    setProcessingIds((prev) => new Set(prev).add(appointmentId));

    setPendingAppointments((prev) => prev.filter((app) => app._id !== appointmentId));
    setAcceptedAppointments((prev) => [...prev, { ...pendingAppointments.find((app) => app._id === appointmentId), status: "accepted" }]);

    try {
      await api.put(`/appointments/${appointmentId}/accept`, {}, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
    } catch (error) {
      setError(`Failed to accept appointment: ${error.response?.data?.error || error.message}`);
      await fetchAppointments();
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(appointmentId);
        return newSet;
      });
    }
  };

  const handleReject = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRejectModal(true);
    setRejectReason("");
  };

  const handleRejectSubmit = async () => {
    if (!selectedAppointment || processingIds.has(selectedAppointment._id)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedAppointment._id));

    setPendingAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));

    try {
      await api.put(`/appointments/${selectedAppointment._id}/reject`, { reason: rejectReason }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setShowRejectModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      setError(`Failed to reject appointment: ${error.response?.data?.error || error.message}`);
      await fetchAppointments();
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedAppointment._id);
        return newSet;
      });
    }
  };

  const handleReschedule = (appointment) => {
    setSelectedAppointment(appointment);
    setShowRescheduleModal(true);
    setRescheduleData({ date: "", time: "", reason: "" });
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment || processingIds.has(selectedAppointment._id)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedAppointment._id));

    const updatedApp = {
      ...selectedAppointment,
      status: "rescheduled",
      rescheduledDate: `${rescheduleData.date}T${rescheduleData.time}:00`,
    };
    setPendingAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    setRescheduledAppointments((prev) => [...prev, updatedApp]);

    try {
      const newDateTime = `${rescheduleData.date}T${rescheduleData.time}:00`;
      await api.put(`/appointments/${selectedAppointment._id}/reschedule`, { 
        date: newDateTime, 
        reason: rescheduleData.reason 
      }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchAppointments();
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      setError(`Failed to reschedule appointment: ${error.response?.data?.error || error.message}`);
      await fetchAppointments();
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedAppointment._id);
        return newSet;
      });
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await api.get(`/appointments/doctor/${userid}`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      }).catch(err => {
        if (err.response?.status === 404) {
          return { data: [] }; // Handle 404 gracefully
        }
        throw err;
      });
      const appointments = response.data || [];

      const pending = appointments.filter(
        (app) => app.status.toLowerCase() === "pending" && new Date(app.rescheduledDate || app.date) >= currentDate
      );
      const accepted = appointments.filter(
        (app) => app.status.toLowerCase() === "accepted" && new Date(app.rescheduledDate || app.date) >= currentDate
      );
      const rescheduled = appointments.filter(
        (app) => app.status.toLowerCase() === "rescheduled" && new Date(app.rescheduledDate || app.date) >= currentDate
      );
      const past = appointments.filter(
        (app) => new Date(app.rescheduledDate || app.date) < currentDate
      );

      setPendingAppointments(pending);
      setAcceptedAppointments(accepted);
      setRescheduledAppointments(rescheduled);
      setPastAppointments(past);
      setTotalAppointments(appointments.length);

      const completedAppointments = appointments.filter(app => app.status.toLowerCase() === "completed");
      const netEarnings = completedAppointments.reduce((sum, app) => {
        const fee = app.consultationFee || 0;
        const commissionRate = app.doctorCommissionRate || 10;
        const commission = (fee * commissionRate) / 100;
        const netEarningsForApp = fee - commission;
        return sum + netEarningsForApp;
      }, 0);
      setTotalEarnings(netEarnings);
    } catch (error) {
      setError("Failed to load appointments: " + (error.response?.data?.error || error.message));
    }
  };

  const handleStartConsultation = (appointmentId) => {
    window.location.href = `/doctor/video-call/${appointmentId}`;
  };

  const getEffectiveDate = (appointment) => {
    return appointment.rescheduledDate ? new Date(appointment.rescheduledDate) : new Date(appointment.date);
  };

  const formatAvailability = (availability) => {
    if (!availability || Object.keys(availability).length === 0) return "No availability set.";
    return Object.entries(availability)
      .map(([day, slots]) => `${day}: ${slots.join(", ")}`)
      .join("; ");
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (error) {
    return (
      <div className="doctor-dashboard-container">
        <h2>{error}</h2>
        {error.includes("Unauthorized") && <button onClick={() => navigate('/doctor-login')}>Go to Login</button>}
      </div>
    );
  }

  return (
    <div className="doctor-dashboard-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="doctor-dashboard-content">
        {isLoading ? (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading your TureMedicine dashboard...</p>
          </div>
        ) : (
          <>
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              {sidebarOpen ? "✕" : "☰"}
            </button>
            {doctor.accountStatus === 'active' ? (
              <>
                <div className="top-section">
                  <div className="doctor-info">
                    <h2>
                      Welcome, Dr. {doctor?.name || "Doctor"}{" "}
                      {doctor.isVerified && (
                        <span className="verified-badge" title="Verified Doctor">
                          ✔ Verified
                        </span>
                      )}
                    </h2>
                    <p>Specialization: {doctor?.specialization || "N/A"}</p>
                    <p>Consultation Fee: ₹{doctor?.consultationFee || 0}</p>
                    <p>Availability: {formatAvailability(doctor?.availability)}</p>
                    {!doctor.isVerified && (
                      <div className="verification-message">
                        <span className="unverified-badge">Unverified</span>
                        <p>Your account is pending verification by the admin team. This typically takes a few hours. You can still update your profile below.</p>
                      </div>
                    )}
                    <button onClick={() => navigate("/doctor-profile")}>Edit Profile</button>
                  </div>
                  <div className="stats-section">
                    <div className="stat-card">
                      <h3>Total Appointments</h3>
                      <p>{totalAppointments}</p>
                    </div>
                    <div className="stat-card">
                      <h3>Total Net Earnings</h3>
                      <p>₹{totalEarnings.toFixed(2)}</p>
                    </div>
                  </div>
                </div>

                {doctor.isVerified ? (
                  <>
                    <h2 className="upcoming-appointments-heading">Upcoming Appointments</h2>
                    <div className="appointments-container">
                      <div className="appointment-column">
                        <h3>Pending Appointments</h3>
                        <div className="appointments-list">
                          {pendingAppointments.length > 0 ? (
                            pendingAppointments.map((app) => (
                              <div key={app._id} className="appointment-card">
                                <p><strong>Patient:</strong> {app.name || "Unknown Patient"}</p>
                                <p>Age: {app.age}</p>
                                <p>Gender: {app.gender}</p>
                                <p>Symptoms: {app.symptoms}</p>
                                <p>Effective Date: {getEffectiveDate(app).toLocaleString()}</p>
                                <button 
                                  onClick={() => handleAccept(app._id)} 
                                  disabled={processingIds.has(app._id)}
                                >
                                  {processingIds.has(app._id) ? "Processing..." : "Accept"}
                                </button>
                                <button 
                                  onClick={() => handleReject(app)} 
                                  disabled={processingIds.has(app._id)}
                                >
                                  {processingIds.has(app._id) ? "Processing..." : "Reject"}
                                </button>
                                <button 
                                  onClick={() => handleReschedule(app)} 
                                  disabled={processingIds.has(app._id)}
                                >
                                  {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                                </button>
                              </div>
                            ))
                          ) : (
                            <p>No pending appointments.</p>
                          )}
                        </div>
                      </div>
                      <div className="appointment-column">
                        <h3>Accepted Appointments</h3>
                        <div className="appointments-list">
                          {acceptedAppointments.length > 0 ? (
                            acceptedAppointments.map((app) => (
                              <div key={app._id} className="appointment-card">
                                <p><strong>Patient:</strong> {app.name || "Unknown Patient"}</p>
                                <p>Age: {app.age}</p>
                                <p>Gender: {app.gender}</p>
                                <p>Symptoms: {app.symptoms}</p>
                                <p>Effective Date: {getEffectiveDate(app).toLocaleString()}</p>
                                <button onClick={() => handleStartConsultation(app._id)}>Start Consultation</button>
                              </div>
                            ))
                          ) : (
                            <p>No accepted appointments.</p>
                          )}
                        </div>
                      </div>
                      <div className="appointment-column">
                        <h3>Rescheduled Appointments</h3>
                        <div className="appointments-list">
                          {rescheduledAppointments.length > 0 ? (
                            rescheduledAppointments.map((app) => (
                              <div key={app._id} className="appointment-card">
                                <p><strong>Patient:</strong> {app.name || "Unknown Patient"}</p>
                                <p>Age: {app.age}</p>
                                <p>Gender: {app.gender}</p>
                                <p>Symptoms: {app.symptoms}</p>
                                <p>Rescheduled Date: {new Date(app.rescheduledDate).toLocaleString()}</p>
                                <button onClick={() => handleStartConsultation(app._id)}>Start Consultation</button>
                              </div>
                            ))
                          ) : (
                            <p>No rescheduled appointments.</p>
                          )}
                        </div>
                      </div>
                      <div className="appointment-column">
                        <h3>Past Appointments</h3>
                        <div className="appointments-list">
                          {pastAppointments.length > 0 ? (
                            pastAppointments.map((app) => (
                              <div key={app._id} className="appointment-card">
                                <p><strong>Patient:</strong> {app.name || "Unknown Patient"}</p>
                                <p>Age: {app.age}</p>
                                <p>Gender: {app.gender}</p>
                                <p>Symptoms: {app.symptoms}</p>
                                <p>Effective Date: {getEffectiveDate(app).toLocaleString()}</p>
                                <p className={`completion-status ${app.status.toLowerCase()}`}>
                                  {app.status.toLowerCase() === "completed" ? "✓ Completed" : "⏳ Consultation Pending"}
                                </p>
                                {(app.status.toLowerCase() === "pending" || app.status.toLowerCase() === "accepted") && (
                                  <button 
                                    onClick={() => handleReschedule(app)} 
                                    disabled={processingIds.has(app._id)}
                                  >
                                    {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                                  </button>
                                )}
                              </div>
                            ))
                          ) : (
                            <p>No past appointments.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="limited-access-message">
                    <p>Your account is pending verification. Once verified, you&apos;ll be able to manage appointments here.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="deactivation-message">
                <h2>Your account is {doctor.accountStatus}.</h2>
                <p>Reason: {doctor.deactivationReason || doctor.deletionReason}</p>
                <p>Please contact our support team at turemedicine@gmail.com for further assistance.</p>
              </div>
            )}

            {showRescheduleModal && (
              <div className="modal">
                <div className="modal-content">
                  <h3>Reschedule Appointment</h3>
                  <div className="form-group">
                    <label htmlFor="rescheduleDate">New Date:</label>
                    <input
                      type="date"
                      id="rescheduleDate"
                      value={rescheduleData.date}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rescheduleTime">New Time:</label>
                    <input
                      type="time"
                      id="rescheduleTime"
                      value={rescheduleData.time}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rescheduleReason">Reason:</label>
                    <select
                      id="rescheduleReason"
                      value={rescheduleData.reason}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, reason: e.target.value })}
                      required
                    >
                      <option value="">Select a reason</option>
                      <option value="Doctor Unavailable">Doctor Unavailable</option>
                      <option value="Emergency">Emergency</option>
                      <option value="Patient Request">Patient Request</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <button 
                    onClick={handleRescheduleSubmit} 
                    disabled={processingIds.has(selectedAppointment?._id)}
                  >
                    {processingIds.has(selectedAppointment?._id) ? "Processing..." : "Confirm"}
                  </button>
                  <button onClick={() => setShowRescheduleModal(false)}>Cancel</button>
                </div>
              </div>
            )}

            {showRejectModal && (
              <div className="modal">
                <div className="modal-content">
                  <h3>Reject Appointment</h3>
                  <div className="form-group">
                    <label htmlFor="rejectReason">Reason for Rejection:</label>
                    <textarea
                      id="rejectReason"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows="4"
                      placeholder="Please provide a reason for rejecting this appointment"
                      required
                    />
                  </div>
                  <button 
                    onClick={handleRejectSubmit} 
                    disabled={processingIds.has(selectedAppointment?._id)}
                  >
                    {processingIds.has(selectedAppointment?._id) ? "Processing..." : "Confirm"}
                  </button>
                  <button onClick={() => setShowRejectModal(false)}>Cancel</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DoctorDashboard;